import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { newIdempotencyKey } from '../lib/api.js';
import { useToast } from '../components/Toast.jsx';
import { useSession } from './session.jsx';

export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * Mutação com:
 * - chave de idempotência mantida até o sucesso (repetir após falha de rede não duplica);
 * - invalidação das consultas do lar indicadas em `invalidate`;
 * - aviso de sucesso somente após confirmação do servidor e aviso de erro legível.
 */
export function useApiMutation(fn, { invalidate = [], success, onSuccess, onError, silentError } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { householdId } = useSession();
  const keyRef = useRef(null);

  return useMutation({
    mutationFn: (vars) => {
      keyRef.current ??= newIdempotencyKey();
      return fn(vars, keyRef.current);
    },
    onSuccess: async (data, vars) => {
      keyRef.current = null;
      await Promise.all(
        invalidate.map((k) => qc.invalidateQueries({ queryKey: k === 'me' ? ['me'] : [householdId, k] })),
      );
      const msg = typeof success === 'function' ? success(data, vars) : success;
      if (msg) toast.success(msg);
      onSuccess?.(data, vars);
    },
    onError: (err, vars) => {
      // Erros de validação/conflito são definitivos para aquele conteúdo: nova tentativa usa nova chave.
      if (err?.status && err.status < 500) keyRef.current = null;
      if (!silentError) toast.error(err.message ?? 'Não foi possível salvar.');
      onError?.(err, vars);
    },
  });
}

/** Estado de formulário simples com erros de campo vindos da API. */
export function useForm(initial) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const set = useCallback((name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  }, []);
  const bind = useCallback(
    (name) => ({
      name,
      value: values[name] ?? '',
      onChange: (e) => set(name, e.target.type === 'checkbox' ? e.target.checked : e.target.value),
    }),
    [values, set],
  );
  return { values, setValues, set, bind, errors, setErrors };
}

export function useDocumentTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} · Lar em Dia` : 'Lar em Dia';
  }, [title]);
}
